import { StateService } from '@uirouter/angular';

import { Injectable } from '@angular/core';
import * as _ from 'lodash';

import { GfTranslateService } from '../../../../services/core.translate.service';
import { ProjectService } from '../../../../services/project.service';
import { UserService } from '../../../../services/core.user.service';
import { ProjectReportsData } from '../../services/project.reports.data.service';
import { FrsReleaseService } from '../../../../../plugins/frs/services/frs.release.service';
import { FilterService } from '../../../../services/core.filter.service';
import { TrackerService } from '../../../../../plugins/tracker/services/tracker.service';
import { UiService } from '../../../../services/core.ui.service';

@Injectable()
export class TrackerReportHelper {

    constructor(
        private stateService: StateService,
        private gfTranslateService: GfTranslateService,
        private projectService: ProjectService,
        private userService: UserService,
        private projectReportsData: ProjectReportsData,
        private frsReleaseService: FrsReleaseService,
        private filterService: FilterService,
        private trackerService: TrackerService,
        private uiService: UiService
    ) {}

    getAnyFilterObj(): any {
        return { 
            id: '', 
            label: this.gfTranslateService.translate("Any"), 
            defaultSelected: true 
        };
    }

    getCustomFilterObj(): any {
        return { 
            id: null, 
            label: this.gfTranslateService.translate("Custom"), 
            defaultSelected: false 
        };
    }

    getYesNoElements(): any[] {
        return [
            { id: 'N', label: this.gfTranslateService.translate('No'), defaultSelected: true },
            { id: 'Y', label: this.gfTranslateService.translate('Yes') }
        ];
    }

    getUserElements(items: any[]): any[] {
        return [this.getAnyFilterObj()]
            .concat(_.map(items, (item) => this.getElementFromUser(item)));
    }

    getElementFromUser(user: any): any {
        return {
            id: user.id,
            label: this.userService.fullName(user)
        };
    }

    getRangeDateElements(): any[] {
        return [
            this.getAnyFilterObj(),
            { id: '-7 days', label: "This Week" },
            { id: '-15 days', label: "Last 15 days" },
            { id: '-30 days', label: "Last 30 days" },
            { id: '-365 days', label: "Last year" }
        ];
    }

    getTrackerElements(items: any[]): any[] {
        return [this.getAnyFilterObj()]
            .concat(_.map(items, (item) => this.getElementFromTracker(item)));
    }

    getElementFromTracker(tracker: any): any {
        return { 
            id: tracker.tracker_id, 
            label: tracker.tracker_name, 
            defaultSelected: false 
        };
    }

    getSprintElements(items: any[]): any[] {
        return [this.getAnyFilterObj()]
            .concat(_.map(items, (item) => this.getElementFromSprint(item)));
    }

    getElementFromSprint(sprint: any): any {
        const label = this.gfTranslateService.translate('Sprint {{sprintProgression}}', sprint);
        return { 
            id: sprint.id, 
            label: label, 
            defaultSelected: false, 
            statusId: sprint.sprintStatus 
        };
    }

    getReleaseElements(items: any[]): any[] {
        return [this.getAnyFilterObj()]
            .concat(_.map(items, (item) => this.getElementFromRelease(item)));
    }

    getElementFromRelease(release: any): any {
        return { 
            id: release.id, 
            label: release.releaseName, 
            defaultSelected: false, 
            statusId: release.isReleased === 'N' ? 0 : 1 
        };
    }

    getBaseFilters(project: any): Promise<any[]> {
        const any = this.getAnyFilterObj();

        const promises = {
            members: this.projectReportsData.getProjectMembers(),
            sprints: this.projectService.sprints(this.stateService.params.unixName, { page_size: 0 }),
            releases: this.frsReleaseService.fetch(this.stateService.params.unixName)
        };

        return Promise.all([promises.members, promises.sprints, promises.releases])
            .then((results: any[]) => {
                const [members, sprints, releases] = results;

                const memberElements = this.getUserElements(members || []);
                const trackerElements = this.getTrackerElements(_.get(project, 'rel.trackers', []));

                // The function expects an object with property fieldElements instead of the raw array
                const sprintElements = this.trackerService.fields.getSprintsGroupedByStatus({
                    fieldElements: this.getSprintElements(_.get(sprints, 'items', []))
                });

                // The function expects an object with property fieldElements instead of the raw array
                const releaseElements = this.trackerService.fields.getReleasesGroupedByStatus({
                    fieldElements: this.getReleaseElements(_.get(releases, 'items', []))
                });

                const filters: any[] = [
                    { alias: "assignee", fieldName: "Assignee", value: any, fieldType: 1, isExtraField: false, fieldElements: memberElements },
                    { alias: "submitter", fieldName: "Submitter", value: any, fieldType: 1, isExtraField: false, fieldElements: memberElements },
                    { alias: "participant", fieldName: "Participant", value: any, fieldType: 1, isExtraField: false, fieldElements: memberElements },
                    { alias: "tracker", fieldName: "Trackers", value: any, fieldType: 1, isExtraField: false, fieldElements: trackerElements },
                    { alias: "releases", fieldName: "Releases", value: any, fieldType: 1, isExtraField: false, fieldElements: releaseElements },
                    { alias: "sprint", fieldName: "Sprints", value: any, fieldType: 1, isExtraField: false, fieldElements: sprintElements },
                    { alias: "rangeDate", fieldName: "Date Range", value: any, fieldType: 1, isExtraField: false, fieldElements: this.getRangeDateElements() }
                ];

                return filters;
            })
            .catch(() => {
                const msg = this.gfTranslateService.translate('There was a problem loading data');
                this.uiService.message.showWarning(msg, 'Data Error');
                return [];
            });
    }

    getSelectedElement(filter: any, stateParams: any): any {
        const val = _.get(stateParams, filter.alias, false) || '';
        let elem = _.find(filter.fieldElements, { id: val });
        if (!elem) {
            elem = _.find(filter.fieldElements, { defaultSelected: true });
        }
        return elem;
    }

    setFiltersFromStateParams = this.filterService.setFilterValues;

    getChartsData(items: any): any {
        const ret: any = {
            statusChartData: null,
            trackerChartData: false,
            assigneeChartData: false,
            submitterChartData: false
        };

        if (items.status) {
            ret.statusChartData = this.projectReportsData
                .setPieChartData(items.status)
                .map((row: any) => {
                    row.key = row.key === 1 ? 'open' : 'closed';
                    return row;
                });
        }

        if (items.tracker) {
            ret.trackerChartData = this.projectReportsData.setPieChartData(items.tracker);
        }

        if (items.assignee) {
            ret.assigneeChartData = this.projectReportsData.setPieChartData(items.assignee);
        }

        if (items.submitter) {
            ret.submitterChartData = this.projectReportsData.setPieChartData(items.submitter);
        }

        return ret;
    }
}
